import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

// Core authentication functions
export const authService = {
  signIn: async (email: string, password: string) => {
    console.log('signIn called with email:', email);
    try {
      console.log('Attempting to sign in with email:', email);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error('Login error:', error.message);
        toast({
          title: "Erro ao fazer login",
          description: error.message === "Invalid login credentials" 
            ? "Email ou senha inválidos" 
            : error.message,
          variant: "destructive",
        });
        return { error };
      }
      
      console.log('Login successful, user:', data.user?.email);
      toast({
        title: "Login realizado com sucesso",
        description: "Bem-vindo de volta!",
      });
      
      return { error: null };
    } catch (error) {
      console.error('Unexpected error during login:', error);
      toast({
        title: "Erro inesperado",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao fazer login",
        variant: "destructive",
      });
      return { error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },

  signInWithGoogle: async () => {
    try {
      console.log('Attempting to sign in with Google');
      
      // Determinar URL de redirect baseado no ambiente
      const isProduction = window.location.hostname === 'arenaproplayer.com';
      const redirectUrl = isProduction 
        ? 'https://arenaproplayer.com/auth/callback'
        : `${window.location.origin}/auth/callback`;
      
      console.log('🔗 Google OAuth redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
        }
      });
      
      if (error) {
        console.error('Google login error:', error.message);
        toast({
          title: "Erro ao fazer login com Google",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }
      
      // No success toast here as the auth state change listener will handle that
      // after redirecting back from Google
      
      return { error: null };
    } catch (error) {
      console.error('Unexpected error during Google login:', error);
      toast({
        title: "Erro inesperado",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao fazer login com Google",
        variant: "destructive",
      });
      return { error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },

  signUp: async (
    email: string, 
    password: string, 
    nickname: string, 
    role: string,
    preferredGame: string,
    whatsappNumber?: string
  ) => {
    try {
      console.log('Attempting to sign up with email:', email);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nickname,
            role,
            preferred_game: preferredGame,
            whatsapp_number: whatsappNumber
          }
        }
      });
      
      if (error) {
        console.error('Signup error:', error.message);
        toast({
          title: "Erro ao criar conta",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }
      
      console.log('Signup successful');
      
      // Send welcome email - select correct template based on role
      try {
        const template = role === 'mentor' ? 'mentorWelcome' : 'welcome';
        
        await supabase.functions.invoke('send-email', {
          body: {
            to: email,
            template,
            data: {
              nickname,
              role,
              preferredGame,
              appUrl: window.location.origin
            }
          }
        });
        console.log(`${role} welcome email sent`);
      } catch (emailError) {
        console.error('Error sending welcome email:', emailError);
        // Continue even if email fails - the signup was successful
      }
      
      // Esta mensagem é para fallback quando o diálogo customizado não for usado
      toast({
        title: "Conta criada com sucesso!",
        description: "Acesse seu email e verifique a caixa de entrada ou SPAM para confirmar seu email e seguir com acesso.",
        duration: 8000, // Duração maior para dar tempo de ler
      });
      
      return { error: null };
    } catch (error) {
      console.error('Unexpected error during signup:', error);
      toast({
        title: "Erro inesperado",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao criar a conta",
        variant: "destructive",
      });
      return { error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },

  signOut: async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logout realizado com sucesso",
      description: "Até logo!",
    });
  },

  requestPasswordReset: async (email: string) => {
    try {
      console.log('Requesting password reset for:', email);
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      
      if (error) {
        console.error('Password reset error:', error.message);
        toast({
          title: "Erro ao solicitar redefinição de senha",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }
      
      console.log('Password reset email sent');
      toast({
        title: "Email enviado",
        description: "Verifique sua caixa de entrada para redefinir sua senha",
      });
      
      return { error: null };
    } catch (error) {
      console.error('Unexpected error during password reset:', error);
      toast({
        title: "Erro inesperado",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao solicitar redefinição de senha",
        variant: "destructive",
      });
      return { error: error instanceof Error ? error : new Error('Unknown error') };
    }
  }
};
